import React, { useState } from 'react';
import { completeJob, rateJob } from '../services/api';

// Shared reputation shape returned by GET /api/reputation/badge/:userId
// (see src/services/api.ts getBadge). Lifted here so any surface that renders
// trust status (Dashboard's "Your Reputation" card, Settings' "Your Trust
// Status" section) reads from one canonical type instead of redeclaring it.
export interface BadgeData {
  badge_level: 'none' | 'verified' | 'trusted' | 'elite';
  rating_avg: number;
  rating_count: number;
  jobs_completed: number;
  member_since: string;
  disputes_pending: number;
  disputes_upheld: number;
  disputes_dismissed: number;
}

// Pure helper: collapse a contractor's dispute counts into a single
// human-readable line. Returns '' when there are no disputes (caller renders
// nothing). Order in the breakdown: upheld -> dismissed -> under review.
// Text logic mirrors Dashboard's local disputeSummary (color is intentionally
// dropped here; this returns text only).
export function disputeSummary(b: BadgeData): string {
  const pending = b.disputes_pending || 0;
  const upheld = b.disputes_upheld || 0;
  const dismissed = b.disputes_dismissed || 0;
  const total = pending + upheld + dismissed;
  if (total === 0) return '';

  if (total === 1) {
    if (pending === 1) return '1 dispute under review';
    if (upheld === 1) return '1 dispute upheld';
    return '1 dispute dismissed';
  }

  const parts: string[] = [];
  if (upheld > 0) parts.push(`${upheld} upheld`);
  if (dismissed > 0) parts.push(`${dismissed} dismissed`);
  if (pending > 0) parts.push(`${pending} under review`);
  if (upheld === total) return `${total} disputes upheld`;
  if (dismissed === total) return `${total} disputes dismissed`;
  if (pending === total) return `${total} disputes under review`;
  return `${total} disputes — ${parts.join(', ')}`;
}

interface BadgeDisplayProps {
  badgeLevel: 'none' | 'verified' | 'trusted' | 'elite';
  ratingAvg?: number | string;
  ratingCount?: number;
  size?: 'sm' | 'md' | 'lg';
}

export const BadgeDisplay: React.FC<BadgeDisplayProps> = ({ badgeLevel, ratingAvg, ratingCount, size = 'md' }) => {
  const ratingAvgNum = typeof ratingAvg === 'string' ? Number(ratingAvg) : ratingAvg;
  if (badgeLevel === 'none' && !ratingAvgNum) return null;
  const badges: Record<string, { icon: string; label: string; color: string; bg: string; border: string }> = {
    verified: { icon: '🔵', label: 'Verified',  color: '#3b82f6', bg: 'rgba(59,130,246,0.1)',  border: 'rgba(59,130,246,0.3)' },
    trusted:  { icon: '🟡', label: 'Trusted',   color: '#f59e0b', bg: 'rgba(245,158,11,0.1)',   border: 'rgba(245,158,11,0.3)' },
    elite:    { icon: '🏆', label: 'Elite Pro',  color: '#7c3aed', bg: 'rgba(124,58,237,0.1)',   border: 'rgba(124,58,237,0.3)' },
  };
  const fontSizes = { sm: '11px', md: '13px', lg: '15px' };
  const paddings  = { sm: '3px 8px', md: '5px 12px', lg: '7px 16px' };
  const badge = badgeLevel !== 'none' ? badges[badgeLevel] : null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
      {badge && (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: paddings[size], borderRadius: '100px', background: badge.bg, border: `1px solid ${badge.border}`, fontSize: fontSizes[size], fontWeight: 700, color: badge.color }}>
          {badge.icon} {badge.label}
        </span>
      )}
      {ratingAvgNum !== undefined && ratingAvgNum > 0 && (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: fontSizes[size], color: '#475569' }}>
          ⭐ {ratingAvgNum.toFixed(1)}{ratingCount && <span style={{ color: '#94a3b8' }}>({ratingCount})</span>}
        </span>
      )}
    </div>
  );
};

interface MarkCompleteProps {
  quoteRequestId: number;
  businessName: string;
  initialCompletion?: { id: number; rating_submitted: boolean } | null;
}

export const MarkComplete: React.FC<MarkCompleteProps> = ({ quoteRequestId, businessName, initialCompletion }) => {
  const [confirming, setConfirming] = useState(false);
  const [loading, setLoading] = useState(false);
  const [completionId, setCompletionId] = useState<number | null>(initialCompletion?.id ?? null);
  const [stars, setStars] = useState(0);
  const [hovered, setHovered] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [rated, setRated] = useState<boolean>(initialCompletion?.rating_submitted ?? false);
  const [error, setError] = useState('');

  const handleMark = async () => {
    setLoading(true);
    try {
      const res = await completeJob({ quoteRequestId });
      setCompletionId(res.data.completionId);
    } catch (err: any) { setError(err.response?.data?.error || 'Failed'); }
    finally { setLoading(false); }
  };

  const handleRate = async () => {
    if (stars === 0) { setError('Please select a star rating'); return; }
    if (completionId === null) { setError('Missing completion id'); return; }
    setSubmitting(true);
    try {
      await rateJob({ completionId, stars, comment });
      setRated(true);
    } catch (err: any) { setError(err.response?.data?.error || 'Failed'); }
    finally { setSubmitting(false); }
  };

  if (rated) return (
    <div style={{ textAlign: 'center', padding: '20px', background: '#f0fdf4', borderRadius: '10px', border: '1px solid #bbf7d0' }}>
      <div style={{ fontSize: '32px' }}>⭐</div>
      <p style={{ margin: '8px 0 0', fontWeight: 700, color: '#15803d' }}>Thanks for your review!</p>
    </div>
  );

  if (completionId) return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '24px' }}>
      <h3 style={{ margin: '0 0 4px', fontSize: '18px', fontWeight: 800, color: '#0f172a' }}>How did {businessName} do?</h3>
      <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#64748b' }}>Your rating helps other homeowners on QuoteBid.</p>
      <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', margin: '0 0 8px' }}>
        {[1,2,3,4,5].map(n => (
          <button key={n} onClick={() => setStars(n)} onMouseEnter={() => setHovered(n)} onMouseLeave={() => setHovered(0)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '32px',
              transform: (hovered >= n || stars >= n) ? 'scale(1.15)' : 'scale(1)',
              filter: (hovered >= n || stars >= n) ? 'none' : 'grayscale(100%) opacity(40%)',
              transition: 'transform 0.1s' }}>⭐</button>
        ))}
      </div>
      <textarea value={comment} onChange={e => setComment(e.target.value)}
        placeholder="Tell others about your experience (optional)" rows={3} maxLength={500}
        style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '14px', resize: 'vertical', boxSizing: 'border-box', marginBottom: '12px' }} />
      {error && <div style={{ color: '#ef4444', fontSize: '13px', marginBottom: '8px' }}>{error}</div>}
      <button onClick={handleRate} disabled={submitting || stars === 0}
        style={{ width: '100%', padding: '12px', borderRadius: '8px', border: 'none',
          background: 'linear-gradient(135deg,#3b82f6,#10b981)', color: '#fff',
          fontSize: '15px', fontWeight: 700, cursor: 'pointer',
          opacity: (submitting || stars === 0) ? 0.5 : 1 }}>
        {submitting ? 'Submitting...' : 'Submit Rating'}
      </button>
    </div>
  );

  return (
    <div>
      {!confirming
        ? <button onClick={() => setConfirming(true)}
            style={{ padding: '10px 20px', borderRadius: '8px', border: 'none',
              background: 'linear-gradient(135deg,#10b981,#059669)', color: '#fff',
              fontSize: '14px', fontWeight: 700, cursor: 'pointer' }}>
            ✅ Mark Job as Complete
          </button>
        : <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '16px' }}>
            <p style={{ margin: '0 0 12px', fontSize: '14px', color: '#334155' }}>Confirm <strong>{businessName}</strong> completed this job?</p>
            {error && <div style={{ color: '#ef4444', fontSize: '13px', marginBottom: '8px' }}>{error}</div>}
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={handleMark} disabled={loading}
                style={{ padding: '10px 20px', borderRadius: '8px', border: 'none',
                  background: 'linear-gradient(135deg,#3b82f6,#10b981)', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>
                {loading ? 'Confirming...' : 'Yes, mark complete'}
              </button>
              <button onClick={() => setConfirming(false)}
                style={{ padding: '10px 16px', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
      }
    </div>
  );
};

export default BadgeDisplay;
